// stores/userGroups.ts
import { defineStore } from 'pinia';
import type {
  UserGroup,
  GetUserGroupsQuery,
  GetUserGroupsQueryVariables,
  GetUserGroupQuery,
  GetUserGroupQueryVariables,
  CreateUserGroupMutation,
  CreateUserGroupMutationVariables,
  CreateUserGroupDto,
  UpdateUserGroupDto,
  UpdateUserGroupMutation,
  DeleteUserGroupMutation,
  DeleteUserGroupMutationVariables,
  UpdateUserGroupMutationVariables,
  MoveUserGroupMutation,
  MoveUserGroupMutationVariables
} from '@/types/graphql';
import getUserGroups  from '@/graphql/queries/getUserGroups.graphql';
import getUserGroup from '@/graphql/queries/getUserGroup.graphql';
import createUserGroup from '@/graphql/mutations/createUserGroup.graphql';
import updateUserGroup from '@/graphql/mutations/updateUserGroup.graphql';
import deleteUserGroup from '@/graphql/mutations/deleteUserGroup.graphql';
import moveUserGroup from '@/graphql/mutations/moveUserGroup.graphql';
import getRoleAssignableUserGroups from '@/graphql/queries/getRoleAssignableUserGroups.graphql';

/**
 * Manages the user groups
 */
export const useUserGroupsStore = defineStore('userGroups', {
  state: () => {
    return{
      userGroups:  [] as UserGroup[],
      loading: true,
      getAllUserGroupsInFlight: null as Promise<any> | null,
    };
  },

  actions: {
    /**
     * Create a new user group
     *
     * @param createUserGroupDto the user group data to create
     */
    async createUserGroup(createUserGroupDto: CreateUserGroupDto){
      this.loading = true;
      try {
        const res = await storeGraphqlMutation<
          CreateUserGroupMutation,
          CreateUserGroupMutationVariables>({
            mutation: createUserGroup,
            variables: {
              ...createUserGroupDto,
            },
          },
          "Fehler beim Erstellen der Gruppe"
        );
        await this.getAllUserGroups();

        this.loading = false;
        return res;
      } catch (error) {
        await this.getAllUserGroups();
        this.loading = false;
        throw error;
      }
    },
    /**
     * Fetch all user groups
     */
    async getAllUserGroups(){
      if (this.getAllUserGroupsInFlight) {
        return this.getAllUserGroupsInFlight;
      }

      this.getAllUserGroupsInFlight = (async () => {
        this.loading = true;
        try {
          const res = await storeGraphqlQuery<
            GetUserGroupsQuery,
            GetUserGroupsQueryVariables
          >({
              query: getUserGroups,
              variables: {},
            },
            "Fehler beim Laden der Gruppen"
          );
          if(res?.data){
            this.userGroups = res.data.getUserGroups as UserGroup[];
          }
          this.loading = false;
          return res;
        } catch (error) {
          this.loading = false;
          throw error;
        }
      })();

      try {
        return await this.getAllUserGroupsInFlight;
      } finally {
        this.getAllUserGroupsInFlight = null;
      }
    },
    /**
     * Update an existing user group
     *
     * @param updateUserGroupDto the user group data to update
     */
    async updateUserGroup(updateUserGroupDto: UpdateUserGroupDto){
      this.loading = true;
      try {
        const res = await storeGraphqlMutation<
          UpdateUserGroupMutation,
          UpdateUserGroupMutationVariables>({
            mutation: updateUserGroup,
            variables: {
              ...updateUserGroupDto,
            },
          },
        );
        await this.getAllUserGroups();

        this.loading = false;
        return res;
      } catch(error) {
        await this.getAllUserGroups();
        this.loading = false;
        throw error;
      }
    },
    /**
     * Delete a user group
     *
     * @param userGroupId the id of the user group to delete
     */
    async deleteUserGroup(userGroupId: number){
      this.loading = true;
      try {
        const res = await storeGraphqlMutation<
          DeleteUserGroupMutation,
          DeleteUserGroupMutationVariables>({
            mutation: deleteUserGroup,
            variables: {
              id: userGroupId,
            },
          },
          "Fehler beim Löschen der Gruppe"
        );
        await this.getAllUserGroups();
        this.loading = false;
        return res;
      } catch (error) {
        await this.getAllUserGroups();
        this.loading = false;
        throw error;
      }
    },

    /**
     * Move a user group to a new parent group
     * @param groupId The id of the group to be moved
     * @param parentGroupId The id of the new parent group or null for root level
     */
    async moveUserGroup(groupId: number, parentGroupId: number | null) {
      this.loading = true;
      try {
        console.log('moveUserGroup', groupId,':', parentGroupId);
        const res = await storeGraphqlMutation<
          MoveUserGroupMutation,
          MoveUserGroupMutationVariables>({
            mutation: moveUserGroup,
            variables: {
              groupId,
              parentGroupId,
            },
          },
          "Fehler beim Verschieben der Gruppe"
        );
        await this.getAllUserGroups();
        this.loading = false;
        return res;
      } catch (error) {
        console.error('Error moving user group:', error);
        await this.getAllUserGroups();
        this.loading = false;
        throw error;
      }
    },

    /**
     * Get a user group by ID
     *
     * @param id The ID of the user group to fetch
     * @returns Promise with the user group data
     */
    async getUserGroup(id: number) {
      this.loading = true;

      try {
        const res = await storeGraphqlQuery<
          GetUserGroupQuery,
          GetUserGroupQueryVariables
        >(
          {
            query: getUserGroup,
            variables: {
              id,
  },
          },
          "Error while loading user grup"
        );

        this.loading = false;
        return res;
      } catch (error) {
        this.loading = false;
        throw error;
      }
    },

    /**
     * Fetch user groups assignable to roles
     */
    async getRoleAssignableUserGroups() {
      this.loading = true;
      try {
        const res = await storeGraphqlQuery(
          {
            query: getRoleAssignableUserGroups,
            variables: {},
          },
          'Fehler beim Laden der zuweisbaren Gruppen'
        );
        if (res?.data) {
          this.userGroups = res.data.getRoleAssignableUserGroups as UserGroup[];
        }
        this.loading = false;
        return res;
      } catch (error) {
        this.loading = false;
        throw error;
      }
    },
  },
});

